import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { CheckSquare, Search, Inbox as InboxIcon } from 'lucide-react';
import { TasksList } from './TasksList.js';
import { RequestsList } from './RequestsList.js';
import { useApprovalStore } from '@/stores/approvalStore.js';
import { useAskUserStore } from '@/stores/askUserStore.js';
import { useSessionStore } from '@/stores/sessionStore.js';

interface InboxPanelProps {
  hideHeader?: boolean;
}

export function InboxPanel({ hideHeader }: InboxPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const pendingCount = useApprovalStore((state) => state.getPendingCount());
  const pendingPrompts = useAskUserStore((state) => state.getPendingCount());
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const totalRequestsCount = pendingCount + pendingPrompts;

  return (
    <div className="flex flex-col h-full">
      {/* Header - can be hidden when used in mobile view */}
      {!hideHeader && (
        <div className="p-4 border-b border-border/50">
          <h2 className="text-lg font-semibold mb-3">Inbox</h2>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="requests" className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 pt-3 border-b border-border/50">
          <TabsList className="grid w-full grid-cols-2 mb-0">
            <TabsTrigger value="requests" className="gap-2">
              <InboxIcon className="h-4 w-4" />
              Requests
              {totalRequestsCount > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                  {totalRequestsCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <CheckSquare className="h-4 w-4" />
              Delegations
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-hidden">
          <TabsContent value="requests" className="h-full m-0 p-0">
            <RequestsList
              searchQuery={searchQuery}
              onOpenSession={(sessionId) => {
                if (sessionId) {
                  setCurrentSession(sessionId);
                }
              }}
            />
          </TabsContent>

          <TabsContent value="tasks" className="h-full m-0 p-0">
            <TasksList searchQuery={searchQuery} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
